import React from 'react';
import { Autocomplete, Chip, TextField, Tooltip } from '@mui/material';
import {
    FilterOption,
    ScheduledDashboardEmail,
    ScheduledDashboardEmailRecipient,
} from 'components/DashboardSendModal/types';
import { useTranslation } from 'react-i18next';
import { getEmailFromRecipient } from '../../helpers';

type Props = {
    scheduledEmail: ScheduledDashboardEmail;
    updateEmail: (updates: Partial<ScheduledDashboardEmail>) => void;
    possibleRecipients: ScheduledDashboardEmailRecipient[];
    error?: string;
    onErrorChange?: (error: string) => void;
    isLoading?: boolean;
};

/**
 * EmailRecipientsSelector is a component for selecting recipients of a scheduled dashboard email.
 *
 * It provides an autocomplete input that allows users to select from a list of possible recipients (internal users)
 * or enter external email addresses (when allowed: not when the filter option is DEFAULT_FILTERS_RECIPIENT_SCOPES).
 * Selected recipients are displayed as chips with invalid recipients visually indicated.
 *
 * @param scheduledEmail - The current scheduled email configuration, including selected recipients and filter options.
 * @param updateEmail - Callback to update the scheduled email state when recipients change.
 * @param possibleRecipients - Array of possible internal recipients to select from.
 * @param error - Optional error message key for validation feedback.
 * @param isLoading - Optional loading state to disable input while processing.
 */
const EmailRecipientsSelector = ({
    scheduledEmail,
    updateEmail,
    possibleRecipients,
    error = undefined,
    isLoading = false,
}: Props) => {
    const { t } = useTranslation();

    const handleRecipientsChange = (
        value: (string | ScheduledDashboardEmailRecipient)[],
    ) => {
        updateEmail({
            recipients: value.map((recipient) => {
                if (typeof recipient === 'string') {
                    return { externalEmail: recipient };
                }
                return recipient;
            }),
        });
    };

    const isInternal = (recipient: ScheduledDashboardEmailRecipient): boolean =>
        possibleRecipients.some(
            (user) => user.recipientId === recipient.recipientId,
        );

    // An invalid recipient is one that is not internal when the filter option is DEFAULT_FILTERS_RECIPIENT_SCOPES
    const isInvalidRecipient = (
        recipient: ScheduledDashboardEmailRecipient,
    ): boolean =>
        scheduledEmail.filterOption ===
            FilterOption.DEFAULT_FILTERS_RECIPIENT_SCOPES &&
        !isInternal(recipient);

    return (
        <Autocomplete
            multiple
            filterSelectedOptions
            autoSelect
            disabled={isLoading}
            limitTags={2}
            options={possibleRecipients}
            value={scheduledEmail.recipients}
            getOptionLabel={(option) =>
                typeof option === 'string'
                    ? option
                    : getEmailFromRecipient(option)
            }
            noOptionsText={t('sendDashboard.noOptions')}
            onChange={(event, value) => handleRecipientsChange(value)}
            freeSolo={
                scheduledEmail.filterOption !==
                FilterOption.DEFAULT_FILTERS_RECIPIENT_SCOPES
            }
            renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                    const { key, ...extraProps } = getTagProps({
                        index,
                    });
                    const isInvalid = isInvalidRecipient(option);
                    return (
                        <Chip
                            key={key}
                            variant="outlined"
                            label={getEmailFromRecipient(option)}
                            color={isInvalid ? 'error' : 'default'}
                            {...extraProps}
                        />
                    );
                })
            }
            renderInput={(params) => (
                <Tooltip title={t('sendDashboard.recipientTips')}>
                    <TextField
                        {...params}
                        label={t('sendDashboard.recipients')}
                        name="email"
                        type="email"
                        error={!!error}
                        helperText={t(error ?? '')}
                    />
                </Tooltip>
            )}
        />
    );
};

export default EmailRecipientsSelector;
